import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Td,
  Tr,
  Text,
  VStack,
  Icon,
  IconButton,
  useDisclosure,
  useToast,
  Select,
  Tag,
  TagLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { AddIcon, AttachmentIcon, CloseIcon } from "@chakra-ui/icons";
import { FaCloudUploadAlt, FaUserTie } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  CONSULTANT_STATUSES,
  CONSULTANT_STATUS_LABELS,
  CONSULTANT_STATUS_LABEL_KEYS,
  CONSULTANT_STAFFING_STATUSES,
  CONSULTANT_STAFFING_STATUS_LABELS,
  CONSULTANT_STAFFING_STATUS_LABEL_KEYS,
  CONSULTANT_STAFFING_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import { listConsultants, bulkUploadCvs, updateConsultant } from "../../../api/consultantsApi";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  FilterBar,
  FilterSearch,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import CreateConsultantWizardModal from "./CreateConsultantWizardModal";
import ImportConsultantsFromFileModal from "./ImportConsultantsFromFileModal";
import ConsultantDetailModal from "./ConsultantDetailModal";

// Compact filter Select matching the template's 31px controls (mirrors Needs.jsx).
const filterSelectSx = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  lineHeight: "31px !important",
  fontSize: "12px",
  fontWeight: 600,
};

// Map API ConsultantItem (GET /consultants) to table row shape (supports snake_case and camelCase)
const mapConsultantToRow = (consultant) => {
  if (!consultant) return null;
  const id = consultant.id ?? consultant.consultantId ?? consultant.consultant_id;
  return {
    id: id ?? `consultant-${Math.random()}`,
    consultantId: id,
    name: consultant.name || consultant.fullName || consultant.full_name || "",
    title: consultant.title || "",
    minSalary: consultant.minSalary ?? consultant.min_salary ?? null,
    maxSalary: consultant.maxSalary ?? consultant.max_salary ?? null,
    skillsTechnical: consultant.skillsTechnical ?? consultant.skills_technical ?? [],
    skillsFunctional: consultant.skillsFunctional ?? consultant.skills_functional ?? [],
    skillsBusiness: consultant.skillsBusiness ?? consultant.skills_business ?? [],
    availability: consultant.availability ?? consultant.isAvailable ?? "",
    status: consultant.status || "",
    location: consultant.location || "",
    email: consultant.email || "",
    portalAccessStatus: consultant.portalAccessStatus ?? consultant.portal_access_status ?? "inactive",
    linkedUserEmail: consultant.linkedUserEmail ?? consultant.linked_user_email ?? "",
    portalInvitedAt: consultant.portalInvitedAt ?? consultant.portal_invited_at ?? null,
    clientName: consultant.currentClientName ?? consultant.current_client_name ?? (consultant.currentClient && (consultant.currentClient.name || consultant.currentClient.fullName)) ?? (consultant.current_client && (consultant.current_client.name || consultant.current_client.full_name)) ?? "",
    needTitle: consultant.currentAssignmentTitle ?? consultant.current_assignment_title ?? (consultant.currentAssignment && (consultant.currentAssignment.title || consultant.currentAssignment.name)) ?? (consultant.current_assignment && (consultant.current_assignment.title || consultant.current_assignment.name)) ?? "",
    staffingStatus: consultant.staffingStatus ?? consultant.staffing_status ?? "",
    pendingDeleteAt: consultant.pendingDeleteAt ?? consultant.pending_delete_at ?? null,
  };
};

// Portal access status -> dot colorScheme for StatusBadge.
const portalStatusColor = (status) => {
  if (status === "active") return "green";
  if (status === "invited") return "blue";
  if (status === "disabled") return "red";
  return "gray";
};

const Consultants = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPagesFromApi, setTotalPagesFromApi] = useState(null);

  // View mode: "table" (default) | "byStatus" (grouped columns)
  const [viewMode, setViewMode] = useState("table");

  // Filters
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAvailability, setFilterAvailability] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterMinSalary, setFilterMinSalary] = useState("");
  const [filterMaxSalary, setFilterMaxSalary] = useState("");

  // Modal state
  const createModal = useDisclosure();
  const importFileModal = useDisclosure();
  const bulkCvModal = useDisclosure();
  const consultantDetailModal = useDisclosure();
  const [selectedConsultantId, setSelectedConsultantId] = useState(null);

  // Bulk CV upload state
  const [bulkCvFiles, setBulkCvFiles] = useState([]);
  const [bulkCvUploading, setBulkCvUploading] = useState(false);
  const [bulkCvDragActive, setBulkCvDragActive] = useState(false);
  const [bulkCvResult, setBulkCvResult] = useState(null);
  const bulkCvInputRef = useRef(null);

  const fetchData = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setLoading(true);
    }
    try {
      const effectivePageSize = viewMode === "byStatus" ? 500 : pageSize;
      const params = {
        search: search || undefined,
        status: filterStatus || undefined,
        staffingStatus: filterAvailability || undefined,
        page: viewMode === "byStatus" ? 1 : page,
        pageSize: effectivePageSize,
      };
      const res = await listConsultants(params, { buId: selectedBusinessUnitId || undefined });
      const mapped = Array.isArray(res.items)
        ? res.items.map(mapConsultantToRow).filter(Boolean)
        : [];
      setItems(mapped);
      setTotalItems(res.totalItems || 0);
      setTotalPagesFromApi(typeof res.totalPages === "number" ? res.totalPages : null);
    } catch (e) {
      toast({
        title: t("pilotConsultants.list.toasts.loadConsultantsFailed"),
        status: "error",
      });
      setItems([]);
      setTotalItems(0);
      setTotalPagesFromApi(null);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [
    search,
    filterStatus,
    filterAvailability,
    filterLocation,
    filterMinSalary,
    filterMaxSalary,
    page,
    pageSize,
    viewMode,
    selectedBusinessUnitId,
    toast,
    t,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refetch when Business Unit context changes (after BU context is ready)
  useEffect(() => {
    if (!isBUContextLoaded) return;
    setPage(1);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBUContextLoaded, selectedBusinessUnitId]);

  // Auto-refresh consultants every 30 seconds (silent refresh, table view only)
  useEffect(() => {
    if (!isBUContextLoaded || viewMode === "byStatus") return;
    const intervalId = setInterval(() => {
      fetchData({ silent: true });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchData, isBUContextLoaded, viewMode]);

  const isSupportedCvFile = (f) => {
    if (!f) return false;
    const mime = f.type;
    const name = (f.name || "").toLowerCase();
    return (
      mime === "application/pdf" ||
      mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      name.endsWith(".pdf") ||
      name.endsWith(".docx")
    );
  };

  const handleBulkCvFilesSelected = (event) => {
    const selected = Array.from(event.target.files || []);
    const valid = selected.filter(isSupportedCvFile);
    if (valid.length !== selected.length) {
      toast({
        title: t("pilotConsultants.list.toasts.invalidFileType", { defaultValue: "Some files were skipped (only PDF and DOCX are accepted)." }),
        status: "warning",
      });
    }
    setBulkCvFiles((prev) => {
      const combined = [...prev, ...valid];
      return combined.slice(0, 20);
    });
    event.target.value = null;
  };

  const handleBulkCvDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setBulkCvDragActive(true);
    else if (e.type === "dragleave") setBulkCvDragActive(false);
  };

  const handleBulkCvDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkCvDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = Array.from(e.dataTransfer.files);
      const valid = dropped.filter(isSupportedCvFile);
      if (valid.length !== dropped.length) {
        toast({
          title: t("pilotConsultants.list.toasts.invalidFileType", { defaultValue: "Some files were skipped (only PDF and DOCX are accepted)." }),
          status: "warning",
        });
      }
      setBulkCvFiles((prev) => {
        const combined = [...prev, ...valid];
        return combined.slice(0, 20);
      });
    }
  };

  const handleRemoveBulkCvFile = (index) => {
    setBulkCvFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkCvUpload = async () => {
    if (bulkCvFiles.length === 0) return;
    setBulkCvUploading(true);
    try {
      const result = await bulkUploadCvs(bulkCvFiles, "français", {
        buId: selectedBusinessUnitId || undefined,
      });
      setBulkCvResult(result);
      setBulkCvFiles([]);
      setPage(1);
      fetchData();
    } catch (err) {
      toast({
        title: t("common.error", { defaultValue: "Error" }),
        description: t("pilotConsultants.list.toasts.bulkCvError", { defaultValue: "Failed to upload CVs." }),
        status: "error",
        duration: 5000,
      });
    } finally {
      setBulkCvUploading(false);
    }
  };

  const handleBulkCvModalClose = () => {
    setBulkCvFiles([]);
    setBulkCvUploading(false);
    setBulkCvDragActive(false);
    setBulkCvResult(null);
    bulkCvModal.onClose();
  };

  const onCreateSuccess = () => {
    setPage(1);
    fetchData();
  };

  const handleStaffingStatusDrop = async (consultantId, nextStatus) => {
    const current = items.find((item) => (item.consultantId || item.id) === consultantId);
    if (!current) return;
    const previousStatus = current.staffingStatus || "active";
    if (previousStatus === nextStatus) return;

    // Optimistic update
    setItems((prev) => prev.map((item) =>
      (item.consultantId || item.id) === consultantId
        ? { ...item, staffingStatus: nextStatus }
        : item
    ));

    try {
      await updateConsultant(consultantId, { staffingStatus: nextStatus }, {
        buId: selectedBusinessUnitId || undefined,
      });
      toast({
        title: t("pilotConsultants.list.toasts.updateStaffingStatusSuccess", { defaultValue: "Staffing status updated" }),
        status: "success",
      });
    } catch (e) {
      // Rollback
      setItems((prev) => prev.map((item) =>
        (item.consultantId || item.id) === consultantId
          ? { ...item, staffingStatus: previousStatus }
          : item
      ));
      toast({
        title: t("pilotConsultants.list.toasts.updateStaffingStatusFailed", { defaultValue: "Failed to update status" }),
        description: e?.response?.data?.message || t("common.error", { defaultValue: "Error" }),
        status: "error",
      });
    }
  };

  const handleView = (item) => {
    const targetId = item?.consultantId || (typeof item?.id === "number" ? item.id : null);
    if (!targetId) return;
    setSelectedConsultantId(targetId);
    consultantDetailModal.onOpen();
  };

  const totalPages = useMemo(() => {
    return totalPagesFromApi || Math.max(1, Math.ceil(totalItems / pageSize));
  }, [totalPagesFromApi, totalItems, pageSize]);

  // Client-side filters for each visible column
  const filteredItems = useMemo(() => {
    const toLower = (v) => (v || "").toString().toLowerCase();
    return (items || []).filter((item) => {
      const name = toLower(item.name || item.fullName || item.full_name);
      const title = toLower(item.title);
      const availability = toLower(item.availability);
      const status = toLower(item.status);
      const location = toLower(item.location);
      const clientName =
        toLower(
          item.clientName ||
            item.client_name ||
            (item.client && (item.client.name || item.client.fullName))
        );
      const needTitle =
        toLower(
          item.needTitle ||
            item.need_title ||
            (item.need && (item.need.title || item.need.name))
        );

      if (search && ![name, title, clientName, needTitle].some((v) => v.includes(toLower(search)))) {
        return false;
      }
      if (filterStatus && !status.includes(toLower(filterStatus))) return false;
      if (filterAvailability && !availability.includes(toLower(filterAvailability))) return false;
      if (filterLocation && !location.includes(toLower(filterLocation))) return false;

      const minSalary =
        item.minSalary != null
          ? Number(item.minSalary)
          : item.min_salary != null
          ? Number(item.min_salary)
          : null;
      const maxSalary =
        item.maxSalary != null
          ? Number(item.maxSalary)
          : item.max_salary != null
          ? Number(item.max_salary)
          : null;

      if (filterMinSalary) {
        const filterMin = Number(filterMinSalary);
        if (Number.isFinite(filterMin)) {
          if (minSalary == null || minSalary < filterMin) return false;
        }
      }

      if (filterMaxSalary) {
        const filterMax = Number(filterMaxSalary);
        if (Number.isFinite(filterMax)) {
          if (maxSalary == null || maxSalary > filterMax) return false;
        }
      }
      return true;
    });
  }, [
    items,
    search,
    filterStatus,
    filterAvailability,
    filterLocation,
    filterMinSalary,
    filterMaxSalary,
  ]);

  const columns = [
    { key: "name", label: t("pilotConsultants.list.table.name"), width: "13%" },
    { key: "title", label: t("pilotConsultants.list.table.title"), width: "12%" },
    { key: "salaryRange", label: t("pilotConsultants.list.table.salaryRange"), width: "10%" },
    { key: "skills", label: t("pilotConsultants.list.table.skills"), width: "20%" },
    { key: "availability", label: t("pilotConsultants.list.table.availability"), width: "9%" },
    { key: "status", label: t("pilotConsultants.list.table.status"), width: "9%" },
    { key: "location", label: t("pilotConsultants.list.table.location"), width: "9%" },
    { key: "email", label: t("pilotConsultants.list.table.email"), width: "11%" },
    { key: "portal", label: t("pilotConsultants.list.table.portal"), width: "7%" },
  ];

  const renderRow = (item) => {
    const pendingDeleteAt = item.pendingDeleteAt || item.pending_delete_at;
    const isPendingDelete = Boolean(pendingDeleteAt);
    const skillsTechnical = Array.isArray(item.skillsTechnical)
      ? item.skillsTechnical
      : Array.isArray(item.skills_technical)
      ? item.skills_technical
      : [];
    const skillsFunctional = Array.isArray(item.skillsFunctional)
      ? item.skillsFunctional
      : Array.isArray(item.skills_functional)
      ? item.skills_functional
      : [];
    const skillsBusiness = Array.isArray(item.skillsBusiness)
      ? item.skillsBusiness
      : Array.isArray(item.skills_business)
      ? item.skills_business
      : [];
    const totalSkillsCount =
      skillsTechnical.length + skillsFunctional.length + skillsBusiness.length;

    const minSalary =
      item.minSalary != null
        ? item.minSalary
        : item.min_salary != null
        ? item.min_salary
        : null;
    const maxSalary =
      item.maxSalary != null
        ? item.maxSalary
        : item.max_salary != null
        ? item.max_salary
        : null;
    const salaryRange =
      minSalary != null || maxSalary != null
        ? [minSalary, maxSalary]
            .filter((v) => v != null)
            .map((v) => v.toString())
            .join(" - ")
        : "";

    return (
      <Tr
        key={item.id}
        onClick={() => handleView(item)}
        cursor="pointer"
        opacity={isPendingDelete ? 0.5 : 1}
      >
        <Td fontWeight="700" color="portal.text">{item.name}</Td>
        <Td>{item.title}</Td>
        <Td>{salaryRange}</Td>
        <Td sx={{ whiteSpace: "normal !important" }}>
          <HStack spacing="4px" wrap="wrap">
            {skillsTechnical.slice(0, 3).map((skill, idx) => (
              <Tag key={`tech-${idx}`} size="sm" colorScheme="purple" variant="subtle" borderRadius="999px">
                <TagLabel fontSize="10px">{skill}</TagLabel>
              </Tag>
            ))}
            {skillsFunctional.slice(0, 3).map((skill, idx) => (
              <Tag key={`func-${idx}`} size="sm" colorScheme="orange" variant="subtle" borderRadius="999px">
                <TagLabel fontSize="10px">{skill}</TagLabel>
              </Tag>
            ))}
            {skillsBusiness.slice(0, 3).map((skill, idx) => (
              <Tag key={`biz-${idx}`} size="sm" colorScheme="green" variant="subtle" borderRadius="999px">
                <TagLabel fontSize="10px">{skill}</TagLabel>
              </Tag>
            ))}
            {totalSkillsCount > 9 && (
              <Tag size="sm" colorScheme="gray" variant="subtle" borderRadius="999px">
                <TagLabel fontSize="10px">+{totalSkillsCount - 9}</TagLabel>
              </Tag>
            )}
          </HStack>
        </Td>
        <Td>{item.availability || ""}</Td>
        <Td>
          {item.status ? (
            <StatusBadge
              label={getStatusLabel(
                t,
                CONSULTANT_STATUS_LABEL_KEYS,
                CONSULTANT_STATUS_LABELS,
                item.status
              )}
              colorScheme="purple"
            />
          ) : (
            ""
          )}
        </Td>
        <Td>{item.location || ""}</Td>
        <Td>{item.email || ""}</Td>
        <Td>
          <StatusBadge
            label={t(`pilotConsultants.detail.consultantPortal.statusLabels.${item.portalAccessStatus || "inactive"}`, item.portalAccessStatus || "inactive")}
            colorScheme={portalStatusColor(item.portalAccessStatus)}
          />
        </Td>
      </Tr>
    );
  };

  return (
    // Bleed past PortalLayout's padding so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="CO"
        eyebrow={t("pilotConsultants.list.eyebrow", "ASAP PILOT")}
        title={t("pilotConsultants.list.pageTitle")}
        actions={
          <>
            <Select
              value={viewMode}
              onChange={(e) => { setViewMode(e.target.value); setPage(1); }}
              w="170px"
              h="32px"
              flex="0 0 auto"
              aria-label={t("pilotConsultants.list.viewSwitcher.ariaLabel", "Data view")}
              bg="white"
              sx={{
                height: "32px !important",
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
                lineHeight: "32px !important",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              <option value="table">{t("pilotConsultants.list.viewSwitcher.allConsultants")}</option>
              <option value="byStatus">{t("pilotConsultants.list.viewSwitcher.byStatus")}</option>
            </Select>
            <Button
              variant="secondary"
              size="sm"
              h="32px"
              leftIcon={<AttachmentIcon boxSize="11px" />}
              onClick={bulkCvModal.onOpen}
            >
              {t("pilotConsultants.list.actions.bulkUploadCvs", { defaultValue: "Bulk Upload CVs" })}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              h="32px"
              leftIcon={<Icon as={FiUpload} boxSize="13px" />}
              onClick={importFileModal.onOpen}
            >
              {t("pilotConsultants.list.actions.importFromFile")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              h="32px"
              leftIcon={<AddIcon boxSize="9px" />}
              onClick={createModal.onOpen}
            >
              {t("pilotConsultants.list.actions.create")}
            </Button>
          </>
        }
      />

      <Box p="20px">
        <Panel
          title={t("pilotConsultants.list.pageTitle")}
          count={
            loading
              ? t("pilotConsultants.list.summary.loading")
              : t("pilotConsultants.list.summary.total", { count: totalItems })
          }
          filters={
            <FilterBar>
              <FilterSearch
                placeholder={t("pilotConsultants.list.filters.searchPlaceholder")}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
              <Select
                placeholder={t("pilotConsultants.list.filters.statusPlaceholder")}
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
                w="auto"
                minW="160px"
                flex="0 0 auto"
                sx={filterSelectSx}
              >
                {CONSULTANT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {getStatusLabel(t, CONSULTANT_STATUS_LABEL_KEYS, CONSULTANT_STATUS_LABELS, s.value)}
                  </option>
                ))}
              </Select>
              <Input
                placeholder={t("pilotConsultants.list.filters.availabilityPlaceholder")}
                value={filterAvailability}
                onChange={(e) => {
                  setFilterAvailability(e.target.value);
                  setPage(1);
                }}
                w="auto"
                minW="150px"
                flex="0 0 auto"
                h="31px"
                fontSize="12px"
                fontWeight="600"
              />
              <Input
                placeholder={t("pilotConsultants.list.filters.locationPlaceholder")}
                value={filterLocation}
                onChange={(e) => {
                  setFilterLocation(e.target.value);
                  setPage(1);
                }}
                w="auto"
                minW="150px"
                flex="0 0 auto"
                h="31px"
                fontSize="12px"
                fontWeight="600"
              />
              <Input
                type="number"
                placeholder={t("pilotConsultants.list.filters.minSalaryPlaceholder")}
                value={filterMinSalary}
                onChange={(e) => {
                  setFilterMinSalary(e.target.value);
                  setPage(1);
                }}
                w="auto"
                minW="130px"
                flex="0 0 auto"
                h="31px"
                fontSize="12px"
                fontWeight="600"
              />
              <Input
                type="number"
                placeholder={t("pilotConsultants.list.filters.maxSalaryPlaceholder")}
                value={filterMaxSalary}
                onChange={(e) => {
                  setFilterMaxSalary(e.target.value);
                  setPage(1);
                }}
                w="auto"
                minW="130px"
                flex="0 0 auto"
                h="31px"
                fontSize="12px"
                fontWeight="600"
              />
            </FilterBar>
          }
          footer={
            viewMode === "byStatus" ? null : (
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
              />
            )
          }
        >
          {viewMode === "byStatus" ? (
            <Box p="16px">
              {loading ? (
                <Flex align="center" justify="center" py="40px">
                  <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotConsultants.list.summary.loading")}
                  </Text>
                </Flex>
              ) : (
                <Flex gap="14px" overflowX="auto">
                  {CONSULTANT_STAFFING_STATUSES.map((statusDef) => {
                    const itemsForStatus = filteredItems.filter(
                      (item) => (item.staffingStatus || "active") === statusDef.value
                    );
                    const colorScheme = CONSULTANT_STAFFING_STATUS_COLORS[statusDef.value] || "gray";
                    return (
                      <Box
                        key={statusDef.value}
                        flex="0 0 auto"
                        w="240px"
                        bg="portal.soft"
                        border="1px solid"
                        borderColor="portal.line"
                        borderRadius="10px"
                        p="12px"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          const consultantId = Number(e.dataTransfer.getData("text/plain"));
                          if (consultantId) handleStaffingStatusDrop(consultantId, statusDef.value);
                        }}
                      >
                        <HStack justify="space-between" mb="10px">
                          <StatusBadge
                            label={getStatusLabel(t, CONSULTANT_STAFFING_STATUS_LABEL_KEYS, CONSULTANT_STAFFING_STATUS_LABELS, statusDef.value)}
                            colorScheme={colorScheme}
                          />
                          <Box
                            as="span"
                            fontSize="11px"
                            fontWeight="800"
                            color="portal.muted"
                            bg="white"
                            borderWidth="1px"
                            borderColor="portal.line"
                            px="7px"
                            py="2px"
                            borderRadius="999px"
                          >
                            {itemsForStatus.length}
                          </Box>
                        </HStack>
                        <VStack align="stretch" spacing="8px" minH="100px" maxH="calc(100vh - 320px)" overflowY="auto">
                          {itemsForStatus.length === 0 ? (
                            <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                              {t("pilotConsultants.list.byStatus.empty", "-")}
                            </Text>
                          ) : (
                            itemsForStatus.map((item) => {
                              const pendingDeleteAt = item.pendingDeleteAt || item.pending_delete_at;
                              const isPendingDelete = Boolean(pendingDeleteAt);
                              return (
                                <Box
                                  key={item.id}
                                  bg="white"
                                  border="1px solid"
                                  borderColor="portal.line"
                                  borderRadius="10px"
                                  p="10px"
                                  boxShadow="0 1px 2px rgba(16,32,51,.05)"
                                  cursor={isPendingDelete ? "not-allowed" : "grab"}
                                  opacity={isPendingDelete ? 0.6 : 1}
                                  draggable={!isPendingDelete}
                                  onDragStart={(e) => {
                                    e.dataTransfer.setData("text/plain", String(item.consultantId || item.id));
                                    e.dataTransfer.effectAllowed = "move";
                                  }}
                                  onClick={() => handleView(item)}
                                  _hover={{ borderColor: "portal.primary" }}
                                >
                                  <Text fontWeight="700" fontSize="12.5px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                                    {item.name}
                                  </Text>
                                  {item.title && (
                                    <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                      {item.title}
                                    </Text>
                                  )}
                                  {item.clientName && (
                                    <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                      {item.clientName}
                                    </Text>
                                  )}
                                </Box>
                              );
                            })
                          )}
                        </VStack>
                      </Box>
                    );
                  })}
                </Flex>
              )}
            </Box>
          ) : (
            <DataTable
              columns={columns}
              rows={filteredItems}
              loading={loading}
              loadingRows={pageSize > 10 ? 10 : pageSize}
              renderRow={renderRow}
              emptyState={
                <EmptyState
                  icon={<FaUserTie />}
                  title={t("pilotConsultants.list.table.noItems")}
                  hint={t("pilotConsultants.list.emptyHint", "Create or import a consultant to start building your pool.")}
                />
              }
            />
          )}
        </Panel>
      </Box>

      <CreateConsultantWizardModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onSuccess={onCreateSuccess}
      />
      <ImportConsultantsFromFileModal
        isOpen={importFileModal.isOpen}
        onClose={importFileModal.onClose}
        onSuccess={() => {
          setPage(1);
          fetchData();
        }}
      />
      <ConsultantDetailModal
        isOpen={consultantDetailModal.isOpen}
        onClose={() => {
          setSelectedConsultantId(null);
          consultantDetailModal.onClose();
        }}
        consultantId={selectedConsultantId}
      />

      <Modal isOpen={bulkCvModal.isOpen} onClose={handleBulkCvModalClose} size="lg">
        <ModalOverlay />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotConsultants.list.bulkCvModal.title", { defaultValue: "Bulk Upload CVs" })}
          </ModalHeader>
          <ModalCloseButton />
          {bulkCvResult ? (
            <>
              <ModalBody>
                {bulkCvResult.successCount > 0 && (
                  <Box mb={4}>
                    <Flex align="center" gap={2} mb={2}>
                      <Tag colorScheme="green" borderRadius="999px" px={2}>
                        <TagLabel fontSize="11px" fontWeight="800">{bulkCvResult.successCount}</TagLabel>
                      </Tag>
                      <Text fontWeight="700" fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotConsultants.list.bulkCvModal.createdTitle", {
                          count: bulkCvResult.successCount,
                          defaultValue: `consultant(s) created successfully`,
                        })}
                      </Text>
                    </Flex>
                    <VStack align="stretch" spacing={1}>
                      {(bulkCvResult.created || []).map((c) => (
                        <Flex
                          key={c.id}
                          align="center"
                          bg="green.50"
                          px={3}
                          py={1.5}
                          borderRadius="10px"
                          cursor="pointer"
                          _hover={{ bg: "green.100" }}
                          onClick={() => {
                            handleBulkCvModalClose();
                            setSelectedConsultantId(c.id);
                            consultantDetailModal.onOpen();
                          }}
                        >
                          <Text fontSize="13px" fontWeight="600" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {c.name}
                          </Text>
                          {c.title && (
                            <Text fontSize="11px" color="portal.muted" ml={2} sx={{ marginBottom: "0 !important" }}>
                              — {c.title}
                            </Text>
                          )}
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                )}
                {bulkCvResult.errorCount > 0 && (
                  <Box>
                    <Flex align="center" gap={2} mb={2}>
                      <Tag colorScheme="red" borderRadius="999px" px={2}>
                        <TagLabel fontSize="11px" fontWeight="800">{bulkCvResult.errorCount}</TagLabel>
                      </Tag>
                      <Text fontWeight="700" fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotConsultants.list.bulkCvModal.errorsTitle", {
                          count: bulkCvResult.errorCount,
                          defaultValue: `file(s) could not be processed`,
                        })}
                      </Text>
                    </Flex>
                    <VStack align="stretch" spacing={1}>
                      {(bulkCvResult.errors || []).map((err, idx) => (
                        <Box
                          key={`err-${idx}`}
                          bg={err.error === "DUPLICATE_CONSULTANT" ? "orange.50" : "red.50"}
                          px={3}
                          py={2}
                          borderRadius="10px"
                        >
                          <Flex align="center" justify="space-between">
                            <Box>
                              <Text fontSize="13px" fontWeight="600" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                                {err.filename}
                              </Text>
                              {err.error === "DUPLICATE_CONSULTANT" ? (
                                <Text fontSize="11px" color="orange.700" mt={0.5} sx={{ marginBottom: "0 !important" }}>
                                  {t("pilotConsultants.list.bulkCvModal.duplicateMessage", {
                                    name: err.existingName,
                                    defaultValue: `A consultant named "${err.existingName}" already exists`,
                                  })}
                                </Text>
                              ) : (
                                <Text fontSize="11px" color="red.600" mt={0.5} sx={{ marginBottom: "0 !important" }}>
                                  {err.error || t("common.unknownError", { defaultValue: "An error occurred" })}
                                </Text>
                              )}
                            </Box>
                            {err.error === "DUPLICATE_CONSULTANT" && err.existingId && (
                              <Button
                                size="xs"
                                variant="secondary"
                                onClick={() => {
                                  handleBulkCvModalClose();
                                  setSelectedConsultantId(err.existingId);
                                  consultantDetailModal.onOpen();
                                }}
                              >
                                {t("pilotConsultants.list.bulkCvModal.viewExisting", { defaultValue: "View Existing" })}
                              </Button>
                            )}
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </ModalBody>
              <ModalFooter sx={MODAL_FOOTER_SX}>
                <Button variant="primary" onClick={handleBulkCvModalClose}>
                  {t("common.close", { defaultValue: "Close" })}
                </Button>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalBody>
                <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                  {t("pilotConsultants.list.bulkCvModal.description", {
                    defaultValue: "Upload up to 20 CV files (PDF or DOCX) to create consultant profiles automatically.",
                  })}
                </Text>
                <Box
                  border="2px dashed"
                  borderColor={bulkCvDragActive ? "portal.primary" : "portal.line"}
                  borderRadius="10px"
                  p={6}
                  mt={4}
                  textAlign="center"
                  minH="120px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg={bulkCvDragActive ? "portal.active" : "transparent"}
                  transition="all 0.2s"
                  cursor="pointer"
                  onClick={() => bulkCvInputRef.current?.click()}
                  onDragEnter={handleBulkCvDrag}
                  onDragLeave={handleBulkCvDrag}
                  onDragOver={handleBulkCvDrag}
                  onDrop={handleBulkCvDrop}
                  _hover={{ bg: "portal.soft" }}
                >
                  <VStack spacing={2}>
                    <Icon as={FaCloudUploadAlt} boxSize={8} color="portal.muted" />
                    <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                      {t("pilotConsultants.list.bulkCvModal.dropzone", {
                        defaultValue: "Drag & drop CV files here, or click to browse",
                      })}
                    </Text>
                    <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                      {t("pilotConsultants.list.bulkCvModal.accepted", {
                        defaultValue: "PDF, DOCX — max 20 files",
                      })}
                    </Text>
                  </VStack>
                </Box>
                <input
                  type="file"
                  ref={bulkCvInputRef}
                  style={{ display: "none" }}
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  multiple
                  onChange={handleBulkCvFilesSelected}
                />
                {bulkCvFiles.length > 0 && (
                  <VStack align="stretch" spacing={1} mt={4}>
                    {bulkCvFiles.map((f, idx) => (
                      <Flex
                        key={`${f.name}-${idx}`}
                        align="center"
                        justify="space-between"
                        bg="portal.soft"
                        px={3}
                        py={1}
                        borderRadius="10px"
                      >
                        <Text fontSize="13px" isTruncated color="portal.text" sx={{ marginBottom: "0 !important" }}>
                          {f.name}
                        </Text>
                        <IconButton
                          icon={<CloseIcon boxSize="9px" />}
                          size="xs"
                          variant="ghost"
                          aria-label={t("common.remove", { defaultValue: "Remove file" })}
                          onClick={() => handleRemoveBulkCvFile(idx)}
                        />
                      </Flex>
                    ))}
                  </VStack>
                )}
              </ModalBody>
              <ModalFooter sx={MODAL_FOOTER_SX}>
                <Button variant="secondary" mr={3} onClick={handleBulkCvModalClose}>
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleBulkCvUpload}
                  isLoading={bulkCvUploading}
                  isDisabled={bulkCvFiles.length === 0}
                >
                  {t("pilotConsultants.list.bulkCvModal.upload", { defaultValue: "Upload" })}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Consultants;
